import { Appointment, QUEUE_TYPE, TQueueFilter } from "@elixir/types";
import { useVirtualizer } from "@tanstack/react-virtual";
import React from "react";
import {
  QueueCardShimmer,
  SelectedFilterStripComponentUi,
  useGetFilterOptions,
} from "ui";
import useGetQueueConfig from "../../apis/tryst/get-queue-config";
import LeftPanelQueueCard from "../../molecules/cards/queue/empty-state/left-panel-queue-card";
import LeftPanelCard from "../../molecules/cards/queue/left-panel-card";
import useQueueStore from "../../stores/queue-store";
import RequestedAppointmentDropdown from "./requested-appointment";
import { DUMMY_PATIENT } from "../../apis/tryst/get-all-queues";
import getB from "../../utils/bridges/EkaBridge";
import { refreshQueue } from "./bottom-marquee";
import { Button } from "antd";
import { ArrowRotate } from "@elixir/icons";

type TQueueContainerProps = {
  isLoading?: boolean;
  emptyTitle?: string;
  emptySubtitle?: string;
  loadingShimmerPrefix?: "single" | "double" | "none";
  loadingShimmerCompact?: boolean;
  queueType: QUEUE_TYPE;
  queueApts: Appointment[];
};

const LeftQueueContainer = ({
  isLoading,
  loadingShimmerCompact,
  loadingShimmerPrefix,
  queueType,
  queueApts = [],
}: TQueueContainerProps) => {
  const clinicId = useQueueStore((state) => state.clinicId);
  const selectedDate = useQueueStore((state) => state.selectedDate);

  const leftQueueFilter = useQueueStore((state) => state.leftQueueFilter);
  const { data: queueConfigData } = useGetQueueConfig();

  const isFilterApplied = (
    Object.keys(leftQueueFilter) as [keyof TQueueFilter]
  ).some((key) => leftQueueFilter?.[key]?.length > 0);
  const setLeftQueueFilter = useQueueStore((state) => state.setLeftQueueFilter);

  const filterOptions = useGetFilterOptions({
    queueData: queueApts,
    tagRecords: queueConfigData?.data?.tags || [],
    queueType,
  });

  const scrollToAid = useQueueStore((state) => state.scrollToAid);
  const scrollToQueue = useQueueStore((state) => state.scrollToQueue);
  const clearScrollToValues = useQueueStore(
    (state) => state.clearScrollToValues
  );

  const aptIndexToScrollTo = React.useMemo(() => {
    if (scrollToAid && scrollToQueue === queueType) {
      return queueApts.findIndex((apt) => apt.aid === scrollToAid);
    }
    return -1;
  }, [queueApts, queueType, scrollToAid, scrollToQueue]);

  const parentRef = React.useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: queueApts.length || 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 246,
    paddingEnd: 100,
  });
  const items = rowVirtualizer.getVirtualItems();

  React.useEffect(() => {
    if (aptIndexToScrollTo === -1) {
      return;
    }
    setTimeout(() => {
      rowVirtualizer.scrollToIndex(aptIndexToScrollTo, {
        align: "start",
        behavior: "smooth",
      });
    }, 300);

    setTimeout(() => {
      clearScrollToValues();
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aptIndexToScrollTo]);

  return (
    <>
      <SelectedFilterStripComponentUi
        count={queueApts.length}
        queueFilter={leftQueueFilter}
        setQueueFilter={setLeftQueueFilter}
        filterOptions={filterOptions}
      />
      <div
        className="dr-w-full dr-overflow-y-scroll"
        style={{
          height: `calc(100% - 1rem - ${isFilterApplied ? "3rem" : "0rem"})`,
        }}
      >
        {isLoading ? (
          <QueueCardShimmer
            compact={loadingShimmerCompact}
            prefix={loadingShimmerPrefix}
          />
        ) : !isLoading && queueApts.length === 0 ? (
          <>
            {queueType === QUEUE_TYPE.BOOKED ? (
              <RequestedAppointmentDropdown />
            ) : null}
            <div className="dr-w-full dr-h-full dr-flex dr-flex-col dr-items-center dr-justify-center dr-space-y-24">
              <LeftPanelQueueCard queueType={queueType} />
            </div>
          </>
        ) : (
          <div
            ref={parentRef}
            style={{
              height: "100%",
              overflowY: "auto",
              contain: "strict",
            }}
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${items[0]?.start ?? 0}px)`,
                }}
              >
                {queueType === QUEUE_TYPE.BOOKED ? (
                  <RequestedAppointmentDropdown />
                ) : null}
                {items.map((virtualItem) => {
                  const apt = queueApts[virtualItem.index];
                  const isScrollTo = virtualItem.index === aptIndexToScrollTo;

                  const isPatientNotFound = apt.patient.oid === DUMMY_PATIENT.patient.id;

                  return (
                    <div
                      data-aid={apt.aid}
                      key={apt.aid}
                      data-index={virtualItem.index}
                      ref={rowVirtualizer.measureElement}
                      style={{
                        padding: "8px",
                      }}
                      className={isPatientNotFound ? "relative" : ""}
                    >
                      <LeftPanelCard
                        aptData={apt}
                        queueType={queueType}
                        isHighlighted={isScrollTo}
                        hideActionButtons={queueType === QUEUE_TYPE.OTHERS}
                      />
                      {isPatientNotFound && (
                        <div onClick={() => {
                          getB().track("refresh-clicked", {
                            aid: apt.aid,
                            clinicId,
                            date: selectedDate.toString(),
                          })
                          refreshQueue(clinicId, selectedDate);
                        }}
                          className="cursor-pointer">
                          <div className="absolute inset-0 dr-z-100 m-8"
                            style={
                              {
                                borderRadius: "8px",
                                borderBottom: "2px solid rgb(231, 147, 141)",
                              }
                            }>
                          </div>
                          <div className="opacity-100 flex items-center space-x-8 absolute bg-white p-16 dr-text-darwin-primary-primary font-700 text-14" style={{
                            left: 50,
                            bottom: 115,
                            zIndex: 900,
                          }}>
                            <div className="dr-flex dr-items-center dr-justify-center">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                xmlns="http://www.w3.org/2000/svg"
                                className={"dr-w-16 dr-h-16"}
                                fill="red"
                              >
                                <path
                                  d="M7 1.84375C5.0957 1.84375 3.36914 2.85937 2.4043 4.48437C1.46484 6.13477 1.46484 8.14062 2.4043 9.76562C3.36914 11.416 5.0957 12.4062 7 12.4062C8.87891 12.4062 10.6055 11.416 11.5703 9.76562C12.5098 8.14062 12.5098 6.13477 11.5703 4.48437C10.6055 2.85937 8.87891 1.84375 7 1.84375ZM7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195313 8.36914 0.195313 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM5.98438 9.15625H6.59375V7.53125H5.98438C5.62891 7.53125 5.375 7.27734 5.375 6.92187C5.375 6.5918 5.62891 6.3125 5.98438 6.3125H7.20313C7.5332 6.3125 7.8125 6.5918 7.8125 6.92187V9.15625H8.01563C8.3457 9.15625 8.625 9.43555 8.625 9.76562C8.625 10.1211 8.3457 10.375 8.01563 10.375H5.98438C5.62891 10.375 5.375 10.1211 5.375 9.76562C5.375 9.43555 5.62891 9.15625 5.98438 9.15625ZM7 5.5C6.54297 5.5 6.1875 5.14453 6.1875 4.6875C6.1875 4.25586 6.54297 3.875 7 3.875C7.43164 3.875 7.8125 4.25586 7.8125 4.6875C7.8125 5.14453 7.43164 5.5 7 5.5Z"
                                />
                              </svg>
                            </div>
                            <span>Click here to reload patient details</span>
                            <ArrowRotate
                              className={`dr-text-darwin-primary-primary dr-w-14 dr-h-14`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LeftQueueContainer;
